/* ============================================================
   What's in Bioactive Blends? — tab toggle (web component)
   <wibb-blends> builds the pill tab bar from each blend panel's
   data-* attributes, then switches the visible ingredient list on
   click / keyboard. Self-upgrades on insert, so it works for the
   initial render and for Shopify section reloads in the editor.
   ============================================================ */
(function () {
  class WhatsInBioactiveBlends extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLElement | null} */
      this.nav = null;
      /** @type {HTMLElement[]} */
      this.panels = [];
      /** @type {HTMLButtonElement[]} */
      this.tabs = [];
    }

    connectedCallback() {
      this.nav = this.querySelector('.wibb__nav');
      this.panels = /** @type {HTMLElement[]} */ (
        Array.from(this.querySelectorAll('.wibb__blend'))
      );
      this.tabs = [];

      if (!this.nav || this.panels.length === 0) {
        return;
      }

      this.buildTabs();
      this.setAttribute('data-js', '');
      this.activate(0);
    }

    buildTabs() {
      const nav = this.nav;
      if (!nav) return;

      const sectionId = this.dataset.sectionId || 'wibb';
      nav.innerHTML = '';

      this.panels.forEach((panel, index) => {
        const label = panel.dataset.label || '';
        const active = panel.dataset.colorActive;
        const inactive = panel.dataset.colorInactive;

        const panelId = `wibb-${sectionId}-panel-${index}`;
        const tabId = `wibb-${sectionId}-tab-${index}`;
        panel.id = panelId;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('tabindex', '0');
        panel.setAttribute('aria-labelledby', tabId);

        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'wibb__tab';
        tab.id = tabId;
        tab.textContent = label;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        if (active) tab.style.setProperty('--tab-active', active);
        if (inactive) tab.style.setProperty('--tab-inactive', inactive);

        tab.addEventListener('click', () => this.activate(index));
        tab.addEventListener('keydown', (event) => this.handleKeydown(event));

        nav.appendChild(tab);
        this.tabs.push(tab);
      });
    }

    /** @param {number} index */
    activate(index) {
      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
      });
    }

    /** @param {KeyboardEvent} event */
    handleKeydown(event) {
      const target = /** @type {HTMLButtonElement} */ (event.currentTarget);
      const currentIndex = this.tabs.indexOf(target);
      if (currentIndex === -1) return;

      let targetIndex = currentIndex;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          targetIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          targetIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          event.preventDefault();
          targetIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          targetIndex = this.tabs.length - 1;
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.activate(currentIndex);
          return;
        default:
          return;
      }

      this.activate(targetIndex);
      this.tabs[targetIndex].focus();
    }
  }

  if (!customElements.get('wibb-blends')) {
    customElements.define('wibb-blends', WhatsInBioactiveBlends);
  }
})();
